// src/data/caseStudies.js
export const caseStudies = [
  // ───────────────── TAG ─────────────────
  {
    slug: "tag-levy-release-24-hours",
    title: "TAG: Income 1040 — Levy Released in 24 Hours",
    companyKey: "TAG",
    excerpt: "Rapid levy relief after CP504/LT11 notice sequence.",
    taxType: "income_1040",
    issues: ["Levy", "CP504", "LT11/1058"],
    resolution: "Levy Release + Compliance Plan",
    date: "2025-01-07",
    hero: "/assets/cases/levy-release.jpg",
    content: [
      `## Who had the problem
**Profile:** Self-employed installer (sole proprietor) in Arizona, filing Schedule C.  
**Household:** 1 adult, 2 dependents.  
**Cash flow:** Seasonal income with irregular monthly deposits.`,

      `## What was the problem
Fell behind on **2021–2023** quarterlies and year-end balances.  
Sequence of IRS notices received:
* **CP14** – balance due
* **CP501/503** – reminders
* **CP504** – intent to levy
* **LT11 / 1058** – final notice (levy authority)

Two days after the LT11 window closed, the bank reported an impending hold. Client contacted **Tax Advocate Group** the same morning.`,

      `## Balance at intake
**Total owed:** **$28,430**  
* Rough breakdown: **$21,700 tax**, **$3,900 penalties**, **$2,830 interest**
* Tax types involved: **Income (1040)** with Schedule C`,

      `## Who they talked to (at the company)
* Intake specialist: **Alicia R.** (same-day onboarding)
* Case manager: **Kendra L.**
* Enrolled Agent: **Daniel S., EA** (representation/strategy)`,

      `## Immediate solution proposed
**Goal:** Stop the levy exposure and stabilize cash flow within 24 hours.  
**Actions:**
* Same-day **Form 2848 POA** filed and e-faxed
* Rapid financial snapshot (**433-A** summary) prepared
* Direct call to ACS with hardship points (rent, dependents, seasonal income)
* **Good-faith payment** scheduled ($150) to demonstrate compliance
* Short-term filing plan for **2022–2023** finalized`,

      `## Resolution (what was implemented)
**Levy Release + Compliance Plan**  
* **Levy release** granted by ACS the next business day (bank instructed not to honor the hold)
* 2022 & 2023 returns filed within **7 days**
* Placed on **$190/month** streamlined installment (to be re-evaluated after full docs)
* Penalty relief packet prepared for **first-time abatement** on 2021 (submitted after filing catch-up)`,

      `## Timing (how long it took)
* **Day 0:** Intake, POA, ACS call, good-faith payment scheduled  
* **Day 1:** **Levy released**; bank confirms no funds will be seized  
* **Day 3–7:** Returns e-filed; wage & expense substantiation organized  
* **Day 10:** Installment approved; follow-up documentation calendar set`,

      `## Client quote
“**I went from thinking my account would be emptied to sleeping through the night.** They explained each step and got the bank to stand down the next day.”`,

      `## Savings / impact
* **Levy avoided:** Estimated **$3,100** that would have been swept was protected  
* **Cash-flow impact:** Payment set to **$190/month** vs. lump-sum demand  
* **Penalty relief:** First-time abatement request submitted (decision pending at time of writing)`,

      `## Why this worked
* Client could show **immediate compliance** (returns in process + small good-faith payment)
* Documented **hardship factors** (dependents, housing costs, seasonal income)
* **Direct, same-day advocacy** through POA and an organized 433-A summary`,

      `## What to have ready (checklist)
* Last two years of **bank statements** (highlight income/essential expenses)
* Proof of **housing, utilities, insurance, childcare**
* A quick list of **business income & necessary expenses** (even if rough)
* Copies of any **IRS letters** (CP14, CP504, LT11/1058)
* Realistic **monthly amount** you can pay without default`,

      `### Who helped
**Company:** Tax Advocate Group  
**Team:** Alicia R. (Intake), Kendra L. (Case Manager), **Daniel S., EA** (Representation)`,

      `### What to expect next
Client remains current on 2024 estimates. After six on-time payments, the team will reassess for **penalty abatement** outcomes and potential **payment reduction** if income remains soft.`,
    ],
  },
  {
    slug: "tag-offer-in-compromise-low-income-datc",
    title: "TAG: Offer in Compromise — Low-Income DATC",
    companyKey: "TAG",
    excerpt: "DATC OIC with low-income certification and slim RCP.",
    taxType: "income_1040",
    issues: ["Offer in Compromise", "DATC"],
    resolution: "OIC (Doubt as to Collectibility)",
    date: "2025-01-14",
    hero: "/assets/cases/oic-low-income.jpg",
    content: [
      `## Who had the problem
**Profile:** Single filer in California; part-time rideshare + seasonal warehouse work.  
**Household:** 1 adult, 1 dependent.  
**Filing:** 1040 with Schedule C for 1099 income.  
**Cash flow:** Irregular; average deposits fluctuate with seasonality.`,

      `## What was the problem
Multiple years of underpayment and penalties after sporadic estimates. Notices escalated through:  
* **CP14** (balance due)  
* **CP501/503** (reminders)  
* **CP504** (intent to levy)  
Client had briefly entered a payment plan in 2023 but defaulted when hours were cut. After another CP504, client contacted **Tax Advocate Group** to explore an **Offer in Compromise (OIC)** on a **Doubt as to Collectibility (DATC)** basis.`,

      `## Balance at intake
**Total owed:** **$52,870**  
* Approx. **$39,600 tax**, **$8,200 penalties**, **$5,070 interest**  
* **Tax types:** Individual Income (1040) with Schedule C (rideshare & gig work)`,

      `## Financial picture (summary from 433-A(OIC))
**Average monthly income:** ~$2,350 (mix of 1099 + occasional W-2 shifts)  
**Allowable expenses (IRS standards + verified actuals):**  
* Housing & utilities: ~$1,250  
* Food, clothing, misc.: ~$630  
* Transportation (operating): ~$260  
* Health insurance & out-of-pocket: ~$110  
* Other necessary (phone, basic internet): ~$85  
**Net disposable:** ~$15–$30 (varies month-to-month)  
**Assets:**  
* Checking: ~$420 average balance  
* Vehicle: 2009 sedan with high mileage; equity **below exemption**  
* No real property, no retirement, no marketable securities`,

      `## Offer basis proposed
**DATC (Doubt as to Collectibility)** with **Low-Income Certification** (per Form 656 instructions).  
* Client qualified by income level and household size → **no $205 application fee**  
* **No initial payment** required with the offer  
* **No monthly payments due while the offer is pending**`,

      `## RCP snapshot (Reasonable Collection Potential)
* **Net realizable equity in assets:** ~$0 (vehicle under exemption; minimal cash)  
* **Future income:** $0–$30/month disposable → $0 when averaged conservatively  
* **RCP estimate:** **≈ $100** (nominal amount to account for minor cash on hand)  
**Proposed offer:** **$100 total** (lump-sum once accepted)`,

      `## Immediate actions
* Filed **Form 2848** (POA) and pulled wage & income transcripts  
* Verified filing status and reconciled 1099 totals vs. bank deposits  
* Brought current on **2024 estimated payments** (token $25/mo while pending to show good faith)  
* Pre-qualified in OIC tool to confirm **low-income certification** eligibility`,

      `## Package submitted
* **Form 656** (OIC) with **Low-Income Certification** box checked  
* **Form 433-A(OIC)** with 3 months of bank statements, receipts, and mileage logs  
* Letter of explanation: seasonal hours, dependent support, health costs, and lack of equity`,

      `## Timeline
* **Week 0–2:** Gather docs, complete 433-A(OIC), pre-qualify, build offer  
* **Week 3:** OIC mailed; **pending** status after intake at COIC  
* **Month 3:** Examiner assigned; requested 1 updated bank statement and proof of 2024 estimates  
* **Month 5:** Examiner recommendation to accept at **$100** (no fee, no initial)  
* **Month 6–7:** Final acceptance letter issued; 5-year compliance period explained`,

      `## Result
**Offer accepted — $100 total** on ~$52,870 balance.  
* Client remitted **$100** within 5 business days of acceptance  
* IRS lien release processed after posted payment and internal closing  
* Client remains on simple monthly set-aside for current-year estimates`,

      `## Client quote
“**I didn’t think the IRS would ever consider my situation.** Once they explained how low-income OIC works, it finally felt possible. The $100 felt surreal after years of notices.”`,

      `## Savings / impact
* **Compromised balance:** ~$52,770 (before any post-acceptance accruals)  
* **Cash-flow relief:** No installment obligation; only current-year estimates to stay compliant  
* **Stress reduction:** Collection activity paused during review; no new levies while pending`,

      `## Why this worked
* **Low RCP**: Virtually no equity + negligible disposable income  
* **Low-Income Certification**: Waived fee & initial payments; reduced burden during review  
* **Clean package**: Organized 433-A(OIC), consistent bank statements, and realistic household budget  
* **Forward compliance**: Began current-year estimates before submission`,

      `## What to have ready (checklist)
* Last 3–6 months of **bank statements** (all accounts)  
* Proof of **housing, utilities, insurance**, and **transportation** costs  
* Any income proof: **1099 statements, app summaries, W-2 stubs**  
* Copy of **IRS letters** and prior plan terms (if any)  
* A realistic **monthly budget** that you can sustain`,

      `### Who helped
**Company:** Tax Advocate Group  
**Team:** Intake specialist, case manager, and EA handling the OIC build and examiner contact`,

      `### What to expect next
Client must stay **fully compliant for 5 years** post-acceptance (file & pay on time).  
After year one, team will review income changes and keep estimates on track to avoid default.`,
    ],
  },
  {
    slug: "tag-cnc-for-seasonal-worker",
    title: "TAG: Currently-Not-Collectible for Seasonal Worker",
    companyKey: "TAG",
    excerpt: "Seasonal earnings & allowable expenses led to CNC.",
    taxType: "income_1040",
    issues: ["CNC", "Unfiled Years"],
    resolution: "CNC + Filing Catch-Up",
    date: "2025-01-20",
    hero: "/assets/cases/cnc-seasonal.jpg",
    content: [
      `## Who had the problem
**Profile:** Seasonal hospitality worker in Nevada; mixes W-2 resort shifts with 1099 event gigs.  
**Household:** 1 adult, 1 dependent (shared custody).  
**Filing:** 1040 + occasional Schedule C for gig work.  
**Cash flow:** Spikes during spring/summer, slow in winter; irregular deposits.`,

      `## What was the problem
Fell behind on **quarterly estimates** and missed a couple of extensions; **two unfiled years** at intake.  
Recent notices included:  
* **CP14** — balance due  
* **CP501/503** — reminders  
* A new **balance due** building for the most recent year (after late filing)  
Client contacted **Tax Advocate Group** to avoid a payment plan they couldn’t sustain and to explore **Currently-Not-Collectible (CNC)**.`,

      `## Balance at intake
**Total owed:** **$18,900** (all 1040)  
* ~**$13,700 tax**, **$3,100 penalties**, **$2,100 interest**  
* Unfiled years: **2** (returns in progress at intake)`,

      `## Financial picture (summary from 433-F/433-A)
**Average monthly income (seasonalized):** ~$2,150 gross / ~$1,960 net  
**Allowable expenses (IRS standards + verified):**  
* Housing & utilities: ~$1,050  
* Food, clothing, misc.: ~$560  
* Transportation (operating + insurance): ~$240  
* Health (premiums + Rx): ~$85  
* Other necessary (phone/basic internet): ~$75  
**Net disposable:** **negative ~$50 to +$20** depending on month  
**Assets:**  
* Checking average balance: ~$280  
* 2008 hatchback; **equity below exemption**  
* No real property, no retirement funds`,

      `## Immediate actions
* Filed **Form 2848 (POA)** and pulled transcripts.  
* Rapid prep for **two unfiled returns**; got withholding/1099 matched to wage & income.  
* Built **433-F** (streamlined) with seasonalized income and standard expenses.  
* Explained CNC vs. Installment to set expectations (accruals, lien possibility, compliance).`,

      `## Resolution requested
**CNC (Currently-Not-Collectible)** based on **no ability to pay** after allowable expenses.  
Key arguments: seasonal volatility, low asset equity, dependent support, and essential expense levels near or below IRS standards.`,

      `## What was implemented
* **Returns filed** within 10 days (ending delinquency).  
* **CNC approved** by ACS after phone advocacy with POA and documented 433-F.  
* **Collection activity suspended** while in CNC.  
* IRS filed/maintained **notice of federal tax lien** (explained as common with CNC at this balance).  
* Client enrolled in **estimate reminders** to keep current-year clean.`,

      `## Timeline
* **Day 0–3:** POA, transcripts, gather docs, seasonalize income.  
* **Day 4–10:** Prepare & e-file two past-due returns.  
* **Day 11–14:** Submit 433-F; advocate CNC with ACS.  
* **Day 21:** **CNC determination granted**; compliance requirements reviewed.`,

      `## Client quote
“**I couldn’t carry a payment through the slow months.** Getting CNC let me breathe and focus on staying current instead of falling behind again.”`,

      `## Savings / impact
* **No monthly payment** required while CNC is in effect.  
* Prevented default on an unaffordable installment that would likely have failed in winter.  
* **Accruals continue**, but budget stabilized; plan to revisit if income improves.`,

      `## Why this worked
* **Seasonalized income** clearly documented (bank statements + pay history).  
* Expenses at or under **IRS national/local standards**.  
* **Low equity** in assets; no available liquidity.  
* **All returns filed**, eliminating a common CNC denial reason.`,

      `## What to have ready (checklist)
* Last **3–6 months** bank statements showing deposits & essential outflows.  
* Proof of **housing, utilities, insurance**, transportation costs.  
* **W-2/1099** and gig app earnings summaries.  
* Copies of **IRS letters**.  
* Realistic monthly budget you can sustain during slow seasons.`,

      `### Who helped
**Company:** Tax Advocate Group  
**Team:** Intake specialist (docs + returns), case manager (433-F build), **EA** (phone advocacy with ACS).`,

      `### What to expect next
CNC is **not permanent**. IRS may **review in 1–2 years** or sooner if income rises.  
Client will:  
* Stay **current on filing/estimates** to avoid default.  
* Notify the team if income changes so they can reassess options (e.g., partial pay IA or OIC).`,
    ],
  },

  // ───────────────── WYNN ─────────────────
  {
    slug: "wynn-payroll-941-staggered-ia-tfrp-defense",
    title: "Wynn: Payroll 941 — Staggered IA & TFRP Defense",
    companyKey: "WYNN",
    excerpt: "Cash-flow staging, trust-fund exposure mitigation.",
    taxType: "payroll_941",
    issues: ["TFRP", "Installment Agreement"],
    resolution: "Tiered IA + TFRP Strategy",
    date: "2025-02-02",
    hero: "/assets/cases/941-staggered-ia.jpg",
    content: [
      `## Who had the problem
**Profile:** Multi-location catering company in Texas (S-Corp), ~24 employees.  
**Ownership/roles:** Two managing members; day-to-day payroll handled by office manager (left 6 months prior).  
**Cash flow:** Event-driven; large swings month-to-month; vendor terms tight after pandemic years.`,

      `## What was the problem
Missed **Form 941** deposits for multiple quarters after a key staff departure and cash crunch.  
IRS sent a series of balance-due and lien notices; **field revenue officer** assigned.  
Potential exposure for **Trust Fund Recovery Penalty (TFRP)** against the two members and the former office manager.  
Company needed a plan that stabilized cash flow **and** mitigated personal TFRP risk.`,

      `## Balance at intake
**Aggregate 941 liability:** **$148,200**  
* Estimated trust-fund portion (withheld FIT/SS/Med): **~$72,000**  
* Penalties & interest (failure-to-deposit, failure-to-pay): **~$19,600**  
* Quarters impacted: **2023 Q3–Q4, 2024 Q1–Q2**  
A **NFTL (lien)** was filed; RO requested in-person financials within 15 days.`,

      `## Business financial snapshot (433-B summary)
**Gross monthly receipts (seasonalized):** ~$185,000  
**Necessary business outlays:**  
* Payroll/contract labor (current): ~$128,000  
* Food & supplies: ~$24,000  
* Rent/insurance/utilities/vehicle: ~$18,500  
* Other fixed overhead: ~$6,200  
**Net available (average):** ~$8,300 (volatile; negative in two prior months)  
**Assets:**  
* Operating cash average: ~$34,000 (sweeps pre-event, dips post-payroll)  
* Two vehicles with liens; no meaningful equity  
* Equipment: walk-ins, ovens, warmers (auction value low; essential to ops)`,

      `## TFRP posture (Form 4180 planning)
RO indicated interviews under **Form 4180** would be set for the two members and possibly the former office manager.  
Wynn strategy focused on:  
* **Role delineation:** who signed checks/EFTPS, who decided creditor priority  
* **Timeline evidence:** emails showing members sought outside payroll help once errors surfaced  
* **Ordinary business care:** vendor and payroll triage to keep operations alive, preserve jobs  
* **Payment designation:** channeling voluntary amounts to **trust-fund first** to reduce personal exposure`,

      `## Immediate actions
* Filed **POA**; coordinated with RO for a short **collection hold** while financials were assembled  
* Brought **current quarter deposits** into on-time **EFTPS** compliance (weekly cadence)  
* Submitted a tightly budgeted **433-B** with seasonality exhibits  
* Scheduled and prepped for **4180 interviews** (talking points + documentation)  
* Sent first **voluntary designated payment** to trust-fund (TC 670 w/ designation request)`,

      `## Resolution requested
**Staggered Installment Agreement (tiered):**  
* Phase 1 (bridge): **$2,500/mo for 6 months** to stabilize cash flow and keep current quarter clean  
* Phase 2 (step-up): **$6,500/mo** thereafter, subject to seasonal relief month (January) each year  
**TFRP defense/containment:** Limit assertion to the former office manager; if assessed to members, pursue **appeals** with designation history and ordinary-care narrative.`,

      `## What was implemented
* RO approved a **tiered IA** aligned to cash-flow spikes, with a written step-up schedule and a one-month seasonal reduction  
* Taxpayer remained **deposit-current**; RO conditioned IA on strict forward compliance  
* **4180 outcomes:**  
  * Former office manager recommended for assertion (signed EFTPS, made deposit decisions)  
  * Members **not asserted** at this stage (RO notes reflected limited payment-direction authority)  
* **Penalty relief:** Partial **FTD penalty** reduction (~$7,800) based on corrective actions and improved controls  
* Ongoing **designation** of voluntary amounts to trust-fund until fully satisfied`,

      `## Timeline
* **Week 1:** POA; current quarter deposits resumed; 15-day hold secured  
* **Week 2:** 433-B + exhibits submitted; first designated payment posted  
* **Week 3:** 4180 interviews completed; RO signals tiered IA is viable if deposits stay perfect  
* **Week 5:** **Tiered IA accepted**; written terms issued  
* **Month 3:** Partial FTD penalty relief approved; running compliance check passed`,

      `## Client quote
“**We needed room to breathe without the personal hammer dropping.** The staggered plan matched our event calendar and kept us from losing staff.”`,

      `## Savings / impact
* **Cash-flow fit:** $2.5k bridge vs. an immediate $7k+ demand prevented layoffs  
* **Penalty reduction:** ~**$7,800** FTD relief; interest continues but manageable under plan  
* **TFRP containment:** No assessment to members at this time; any later assertion defensible with designation/history`,

      `## Why this worked
* **Forward compliance first:** deposits perfect before asking for terms  
* **Seasonality documented:** calendars, P&L by month, event contracts supported the tiering  
* **Role clarity:** 4180 prep reduced member exposure and focused on the actual decision-maker  
* **Payment designation:** accelerated reduction of the **trust-fund** portion`,

      `## What to have ready (checklist)
* Last **12 months** P&L and bank statements (monthly breakouts)  
* Payroll reports, **EFTPS** history, and deposit calendars  
* Vendor and payroll aging; proof of essential expenses and margins  
* Names/roles for anyone who signed checks/portals; **who decided** which bills got paid  
* Realistic cash-flow model for a tiered plan (show seasonality)`,

      `### Who helped
**Company:** Wynn Tax Solutions  
**Team:** RO liaison & financials (case manager), **EA** for 4180 prep/strategy, negotiations lead for tiered IA`,

      `### What to expect next
* **Quarterly compliance checks** by RO/ACS; any missed deposit can default the IA  
* If the **TFRP** is proposed for others, Wynn will respond at **Appeals** with designation records and ordinary-care evidence  
* After 12 on-time months, revisit for **additional penalty relief** or a payment recalibration if margins stay thin`,
    ],
  },
  {
    slug: "wynn-complex-corp-audit-to-appeals",
    title: "Wynn: Complex Corporate Audit — Strategy Through Appeals",
    companyKey: "WYNN",
    excerpt: "Exam to Appeals with targeted substantiation plan.",
    taxType: "business_1120_1065",
    issues: ["Audit", "Appeals"],
    resolution: "Appeals Concession",
    date: "2025-02-09",
    hero: "/assets/cases/corp-audit-appeals.jpg",
    content: [
      `## Who had the problem
**Profile:** Regional metal fabrication & installation company (C-corp filing **1120**) with ~38 employees.  
**Footprint:** Two shops (TX & OK), mobile install crews.  
**Books/tax:** Job-cost system in QuickBooks; outside CPA prepared returns.`,

      `## What was the problem
An IRS **large & mid-size business** (LMSB) team opened a **field exam** covering two years.  
Initial **IDRs** (Information Document Requests) targeted cost capitalization (**§263A**), repairs vs. improvements (**§162 vs. §263**), **meals/travel substantiation (§274)**, and an **R&D credit (Form 6765)** claimed for fixture prototyping.  
After 6 months of back-and-forth, the Exam team issued a **30-day letter** with significant proposed increases to income and a **§6662 accuracy-related penalty**.`,

      `## Exam posture at intake (what IRS proposed)
**Proposed additional tax (both years): ~** **$428,000**  
**Penalty (20% §6662):** **$85,600** (pre-interest)  
**Key proposed adjustments:**  
* **§263A (UNICAP):** Capitalize **$420,000** of indirect shop costs into ending inventory/absorption  
* **Repairs vs. improvements:** Capitalize **$260,000** of plant work (roof, electrical, slab)  
* **Meals & travel (§274):** Disallow **$160,000** for missing logs/receipts (50% limitation ignored in exam calc)  
* **R&D credit:** Reduce by **$110,000** for lack of sufficient nexus and wage/time documentation`,

      `## Wynn’s exam strategy (before Appeals)
**Goal:** Narrow the adjustments, rebuild defensible documentation, and frame **hazards of litigation** early.  
**Actions:**  
* Performed a focused **§263A cost study** (channels, burden rates, negative absorption tie-outs) with alternative reasonable method under regs.  
* Ran **repairs/ROTU analysis** under the tangible property regs (units of property, BAR tests: betterment, adaptation, restoration); re-classified items to **routine maintenance safe harbor** and de minimis policy where applicable.  
* Reconstructed **§274** support using vendor statements, credit card data, crew tickets, and contemporaneous calendars; separated **travel/lodging** from **50% meals** and removed entertainment.  
* For **R&D**, aligned projects to **qualified research** elements (permitted purposes, process of experimentation), mapped **wage allocations** and supply usage to project lists, created nexus memos and **trial prototypes** logs.  
* Negotiated a last **exam conference** to preview these positions and to request a specialist consult on §263A and R&D.`,

      `## Why move to Appeals
Exam conceded some items but kept major portions of **§263A**, **repairs**, and **R&D**.  
Given precedent and documentation gaps **(pre-engagement)**, **Appeals** offered a better forum to weigh hazards, materiality, and method changes without full litigation costs.`,

      `## Appeals strategy (targeted concessions)
**Positioning:**  
* For **§263A**, proposed an **alternative absorption method** prospectively with a **limited retro adjustment** reflecting materiality thresholds and negative absorption impact on low-margin jobs.  
* For **repairs**, emphasized **routine maintenance**, incremental component tests, and lack of betterment to UoP; offered partial capitalization only for discrete slab replacement.  
* For **§274**, accepted document gaps on older charges but retained items with corroboration; applied the correct **50%** limitation rather than full disallowance.  
* For **R&D**, provided a **project-by-project nexus** (design intent, testing iterations, failures), payroll tie-outs, shop scrap logs, and supply pulls; proposed a **reduced but sustained** credit.  
* **Penalty defense (§6662):** Good-faith reliance on CPA, complexity of §263A/TPR, and timely cooperation → request **full penalty abatement**.`,

      `## Resolution (what was implemented)
**Appeals concession package (both years combined):**  
* **§263A:** Reduction of proposed adj. from **$420k → $140k** (method change agreed prospectively)  
* **Repairs vs. improvements:** From **$260k → $90k** remaining capitalization  
* **§274:** Disallowance trimmed from **$160k → $62k**, with correct 50% meals limitation applied  
* **R&D credit:** Sustained **~70%** of originally claimed amount; **$77k** of credit preserved  
* **§6662 penalty:** **Fully abated** based on reasonable cause and exam cooperation  
**Final additional tax (net of credit): ~** **$136,000** (pre-interest), payable over an **installment** with no lien filing.`,

      `## Timeline
* **Month 0–1:** Wynn onboard; records pull; cost study & TPR analysis kick-off  
* **Month 2:** Final exam conference; partial concessions noted; **Protest** drafted and filed timely  
* **Month 4:** **Appeals** conference; follow-up IDR responses and method memo submitted  
* **Month 6:** Appeals letter of proposed resolution; penalty removal and credit sustainment confirmed  
* **Month 7:** Closing agreement executed; installment terms finalized with Collections`,

      `## Client quote
“**They separated what was actually defensible from what we hoped was defensible.** We kept a big chunk of the R&D credit and avoided the penalty—which was the part that really scared us.”`,

      `## Savings / impact
* **Tax reduction:** From **$428k** proposed → **$136k** final (≈ **$292k** reduced)  
* **Penalty relief:** **$85.6k** §6662 **removed**  
* **R&D preserved:** **~$77k** sustained credit  
* **Cash flow:** Installment terms avoided a lien and protected bonding capacity`,

      `## Why this worked
* **Method over math:** Reframed **§263A** to an acceptable method with limited retro adjustment  
* **TPR fluency:** Unit-of-property and routine-maintenance arguments carried weight at Appeals  
* **Rebuilt support:** Practical **§274** substantiation and clear **project nexus** for R&D  
* **Penalty narrative:** Complexity + CPA reliance + cooperation = strong reasonable-cause case`,

      `## What to have ready (checklist)
* Prior-year **inventory/costing** memos, burden/overhead calculations, and schedule tie-outs  
* Fixed-asset & repairs detail with **invoices, photos, work orders**, and maintenance logs  
* **Travel/meal** statements, calendars, and crew/job cross-references  
* **R&D** project lists, prototypes, engineering notes, wage/time allocations, supply pulls  
* Engagement letters & emails showing **CPA reliance** and advice on complex areas`,

      `### Who helped
**Company:** **Wynn Tax Solutions**  
**Team:** Costing specialist (263A), TPR lead for repairs vs. improvements, senior EA for **Appeals**, and documentation analyst for §274/R&D tie-outs`,

      `### What to expect next
* Adopt the agreed **§263A method** prospectively; document policies in writing  
* Maintain a simple **TPR policy** (de minimis, routine maintenance) and save evidence contemporaneously  
* Track **R&D** activities quarterly (nexus notes, time allocations) to avoid reconstruction later  
* Schedule a mid-year check with **Wynn** to confirm compliance before year-end`,
    ],
  },
  {
    slug: "wynn-peo-transition-941-fallout",
    title: "Wynn: PEO Transition & 941 Fallout",
    companyKey: "WYNN",
    excerpt: "Liability mapping after PEO change and missing deposits.",
    taxType: "payroll_941",
    issues: ["Deposit Penalties", "Lien"],
    resolution: "Penalty Relief + IA",
    date: "2025-02-16",
    hero: "/assets/cases/peo-941.jpg",
    content: [
      `## Who had the problem
**Profile:** Multi-state HVAC service company (S-corp) with **22–28 W-2 employees** depending on season.  
**Change event:** Moved from in-house payroll to a new **PEO** mid-year.  
**Cash flow:** Tight in shoulder months; heavy receivables lag from commercial jobs.`,

      `## What was the problem
During the **PEO transition**, several **941 deposits** were missed or posted under the wrong EIN. The prior provider stopped making deposits once the PEO contract began, but the **PEO onboarding lag** meant two pay periods were processed **off-cycle** (no EFTPS deposits).  
Downstream issues:
* **§6656 Failure-to-Deposit (FTD) penalties** on two quarters  
* **Notice stream:** CP161 → CP220 → **CP504 (Intent to Levy)**  
* A **Notice of Federal Tax Lien** was filed against the S-corp  
* State payroll registrations partially duplicated; SUTA over/under on two states`,

      `## Balance at intake
**Total outstanding (two quarters): ~** **$185,400** tax + **$12,900** penalties + **$3,800** interest  
* Quarter 2: ~$94k tax due; ~$6.8k FTD penalties assessed  
* Quarter 3: ~$91k tax due; ~$6.1k FTD penalties assessed  
* Mix of **semiweekly lookback** & new PEO filing created Schedule B timing errors`,

      `## Who they talked to (at the company)
* Intake lead: **Maya T.** (records pull + transcript request same day)  
* Payroll/PEO specialist: **Andre V.** (mapping deposits & EIN issues)  
* Senior EA: **Carmen P., EA** (Appeals & Collections strategy)`,

      `## Immediate solution proposed
**Goal:** Stop enforced collection, fix the deposit map, and stabilize cash flow.  
**Actions (first 72 hours):**
* Filed **Form 2848 POA** and pulled **account transcripts** (two quarters)  
* Reconciled **EFTPS receipts** from old provider, PEO remittances, and client bank debits  
* Created a **deposit mapping schedule** (who paid what, under which EIN, by date)  
* Called **ACS** to request **hold on enforcement** and a short **$0-down CNC-hold** pending review  
* Drafted **reasonable-cause** narrative specific to **PEO transition** & control procedures`,

      `## Resolution (what was implemented)
**1) Deposit re-mapping & corrections**  
* Submitted **payment tracing** with proof of PEO remittances credited to the wrong account/EIN  
* Corrected **Schedule B timing** for semiweekly periods; removed “deemed late” segments  
* Two misapplied deposits were **moved** to the correct quarter/EIN

**2) Penalty relief package**  
* Argued **reasonable cause** under §6656 for transition error + ordinary business care & prudence  
* Provided **internal control** memo (new approval workflow, PEO calendar, EFTPS cross-check)  
* Result: **~68% FTD penalty abatement** across both quarters (rest reduced via first-time relief on one quarter)

**3) Terms with Collections**  
* Converted temporary hold to a **6-month tiered installment**:  
  * Months 1–3: **$3,500/mo** while receivables caught up  
  * Months 4–6: **$10,000/mo** with balloon to clear residual  
* **Lien withdrawal** after 3 consecutive payments + proof of current compliance (941 deposits on time via PEO)  
* State payroll registrations fixed; **SUTA base** aligned; duplicate account closed`,

      `## Timing (how long it took)
* **Days 0–3:** Transcripts, mapping schedule, ACS hold, CNC-hold granted  
* **Week 2:** Proof packet for re-mapping & reasonable-cause submitted  
* **Week 5:** **Penalty abatement** letter received; deposit moves posted  
* **Week 6:** **Tiered IA** accepted; lien withdrawal milestone set (after 3 on-time payments)`,

      `## Client quote
“**Our move to the PEO was supposed to make life easier—then the letters started.** Wynn figured out which payments went where and got the penalties knocked down. The tiered plan saved cash while we waited on receivables.”`,

      `## Savings / impact
* **Penalty relief:** From **$12.9k → $4.1k** (≈ **$8.8k** reduced)  
* **Cash flow:** Tiered IA prevented a levy and let them avoid expensive bridge financing  
* **Lien:** **Withdrawal** queued after compliance milestone (helps bonding/vendor approvals)`,

      `## Why this worked
* **Forensic deposit map** tied every EFTPS/PEO debit to the correct period/EIN  
* Clear **reasonable-cause** story: transition controls were in progress; errors promptly corrected  
* **Tiered terms** matched receivable cycle; IRS saw realistic capacity and current compliance`,

      `## What to have ready (checklist)
* **PEO agreement**, start date, and first three payroll runs  
* Prior provider’s **final payroll register** + last EFTPS confirmation  
* **Bank debits** and EFTPS receipts for the affected quarters  
* **Schedule B** detail (semiweekly periods) and any **amended 941/940** filings  
* Brief **internal control memo**: who reviews deposits, how you cross-check, and calendar`,

      `### Who helped
**Company:** **Wynn Tax Solutions**  
**Team:** Maya T. (Intake), Andre V. (PEO mapping), **Carmen P., EA** (Collections & penalty relief)`,

      `### What to expect next
* Maintain **on-time semiweekly deposits** via PEO; export & save confirmations monthly  
* After 3 on-time IA payments, submit **lien withdrawal** request with proof of compliance  
* Quarterly check-ins with Wynn to confirm **no deposit gaps** during seasonal slowdowns`,
    ],
  },

  // ──────────────── ATS ────────────────
  {
    slug: "ats-trucker-1099-fresh-start-ia",
    title: "ATS: Trucker 1099 — Fresh Start Installment Agreement",
    companyKey: "ATS",
    excerpt: "Quarterlies miss + mileage substantiation into IA.",
    taxType: "income_1040",
    issues: ["1099", "Installment Agreement"],
    resolution: "Streamlined IA",
    date: "2025-03-01",
    hero: "/assets/cases/trucker-ia.jpg",
    content: [],
  },
  {
    slug: "ats-unfiled-years-streamlined-compliance",
    title: "ATS: Unfiled Years — Streamlined Compliance Path",
    companyKey: "ATS",
    excerpt: "Three-year filing catch-up and safe-harbor planning.",
    taxType: "income_1040",
    issues: ["Unfiled Years"],
    resolution: "Compliance + Payment Plan",
    date: "2025-03-08",
    hero: "/assets/cases/unfiled-streamlined.jpg",
    content: [
      `## Who had the problem
**Profile:** Solo **owner-operator (CDL-A)** leased to a national carrier; over-the-road (**OTR**) routes across the Southwest.  
**Entity:** Schedule C (sole proprietor).  
**Miles:** ~**118,000** annually; uses **ELD** and fuel card.  
**Cash flow:** Weekly settlements with factoring; income swings with fuel and lane rates.`,

      `## What was the problem
No **quarterly estimates** paid for **2022–2023**; year-end balances snowballed with **self-employment tax**.  
Recordkeeping gaps on **per diem days**, maintenance, and tolls.  
IRS notice stream:
* **CP14** – balance due (both years)  
* **CP501/503** – reminder sequence  
* **CP504** – **Intent to Levy** received on 2023  

Client contacted **ATS** after the CP504 to avoid levy and get on a stable plan.`,

      `## Balance at intake
**Running total (pre-filing/adjustments):** about **$26,750 tax**, **$3,400 penalties**, **$1,100 interest** (≈ **$31,250**).  
Mix is heavily **SE tax** plus underpaid regular income tax.`,

      `## Who they talked to (at the company)
* Intake specialist: **Nina S.** (same-day transcripts + organizer)  
* Trucker specialist: **Marco D.** (ELD/fuel/IFTA reconstruction)  
* Senior EA: **Rachel P., EA** (Collections & IA strategy)`,

      `## Immediate solution proposed
**Goal:** Stop levy risk, **reconstruct deductions** accurately, and secure a **Fresh Start IA**.  
**Actions (first week):**
* Filed **Form 2848 POA**; pulled **account & wage transcripts**  
* Requested **collection hold** with ACS tied to pending return corrections  
* Built an **expense/per diem workbook** from **ELD logs**, **fuel card statements**, **maintenance invoices**, and **IFTA** mile summaries  
* Scheduled a small **good-faith payment ($100)** and set expectations for **direct-debit IA**`,

      `## Resolution (what was implemented)
**1) Return cleanup & deductions**  
* Amended 2022 and finalized 2023 using **actual trucking expenses** (fuel, maintenance, insurance, tolls, depreciation/MACRS).  
* Applied **transportation per diem** for documented overnight trips (days away verified from ELD + settlements).  
* Result: **assessed balance adjusted to ≈ $22,300** across the two years.

**2) Fresh Start Installment Agreement**  
* Qualified for **streamlined IA** (balance < $50k), no full financials required.  
* Set up **Direct-Debit IA (DDIA)** at **$185/month (72 months)** with auto-debit; Form 433-D signed.  
* Filed **First-Time Abatement** on one year → **~$1,150** penalty reduction.

**3) Staying current**  
* **EFTPS** quarterly estimates enrolled with calendar reminders (Q1–Q4).  
* Simple **monthly record checklist**: export ELD trip list, save fuel card CSV, scan maintenance receipts.`,

      `## Timing (how long it took)
* **Days 0–3:** POA, transcripts, ACS hold, data requests to carrier/fuel card  
* **Week 2:** Expense & per diem reconstruction complete; filings submitted  
* **Week 3:** **Adjusted balance posted**; **DDIA approved**  
* **Week 5:** **FTA** penalty credit applied`,

      `## Client quote
“**I used to dread the letters.** ATS put the numbers together from my logs and fuel card, got the payment down to something I can live with, and set me up so I don’t fall behind again.”`,

      `## Savings / impact
* **Balance reduction:** From ≈ **$31,250** to **≈ $22,300** after proper deductions and corrections  
* **Penalty relief:** **~$1,150** (FTA)  
* **Cash-flow stability:** **$185/month** vs. lump-sum demand; levy avoided`,

      `## Why this worked
* Solid **reconstruction** from **ELD + fuel + IFTA** to prove ordinary & necessary expenses  
* **Streamlined DDIA** under Fresh Start—quick approval, no deep financials  
* **Forward-looking compliance** (EFTPS estimates + simple monthly checklist)`,

      `## What to have ready (checklist)
* **ELD trip summary** (overnights marked) and **carrier settlements**  
* **Fuel card** statements (CSV if possible), **toll**/scale receipts  
* **Maintenance/repair** invoices and **insurance** declarations  
* **IFTA** mileage quarterly reports  
* Driver’s **home base**, typical lanes, and days away (for per diem)`,

      `### Who helped
**Company:** **American Tax Support (ATS)**  
**Team:** Nina S. (Intake), **Marco D.** (Trucking reconstruction), **Rachel P., EA** (IA & penalties)`,

      `### What to expect next
* Make **on-time quarterly estimates**; ATS checks actual vs. projected each quarter  
* Keep the **monthly log bundle** (ELD export + fuel CSV + receipts)  
* At 6–12 months of on-time payments, revisit payment amount if income softens`,
    ],
  },
  {
    slug: "ats-same-day-bank-levy-escalation",
    title: "ATS: Same-Day Bank Levy Escalation",
    companyKey: "ATS",
    excerpt: "Financial snapshot + immediate advocacy to release funds.",
    taxType: "income_1040",
    issues: ["Levy"],
    resolution: "Levy Release",
    date: "2025-03-15",
    hero: "/assets/cases/bank-levy.jpg",
    content: [
      `## Who had the problem
**Profile:** W-2 + side-gig (rideshare) earner in **Nevada**.  
**Household:** 1 adult, 1 dependent.  
**Cash flow:** Weekly pay + variable app income; checking avg. balance **$1,200–$2,000**.`,

      `## What was the problem
Fell behind on balances after under-withholding and no quarterly estimates on side-gig income.  
Notice trail:
* **CP14** – balance due  
* **CP501/503** – reminders  
* **CP504** – **Intent to Levy**  
* Bank reported **legal order hold** the morning client’s rent was due

Client contacted **American Tax Support (ATS)** the same day the bank placed the hold.`,

      `## Balance at intake
**Assessed total:** ≈ **$18,900**  
* Tax: **$14,600**  
* Penalties: **$2,300**  
* Interest: **$2,000**  
**Funds on hold:** Bank reported **$2,750** frozen under levy.`,

      `## Who they talked to (at the company)
* Intake coordinator: **Jasmine K.** (same-day onboarding + transcript pull)  
* Case manager: **Owen L.** (bank + IRS coordination)  
* Enrolled Agent: **Priya R., EA** (financial snapshot & advocacy)`,

      `## Immediate solution proposed
**Goal:** Avoid loss of rent funds; secure **same-day levy release**, then stabilize.  
**Actions (hour 0–6):**
* Filed **Form 2848 POA**; pulled **account transcripts**  
* Built **rapid 433-A snapshot** (rent, childcare, transport, net income)  
* Called **ACS** to request **levy release** citing **economic hardship** and pending compliance  
* Bank **fax numbers** + release instructions confirmed in advance  
* Scheduled **$50 good-faith payment** and set a **direct-debit date** to demonstrate intent`,

      `## Resolution (what was implemented)
**Levy Release** (same day):  
* ACS granted **immediate release** based on hardship and verified living-expense data  
* **Release faxed** to bank before close of business; bank confirmed **hold lifted** and funds available next morning  

**Stabilization Plan:**  
* Withholding updated at employer; side-gig placed on **monthly EFTPS set-aside**  
* Short return cleanup checklist (W-2 + 1099 substantiation) prepared for the EA  
* Temporary **collection hold** noted while filings updated; IA options explained (target: <$150/mo)`,

      `## Timing (how long it took)
* **Hour 0–1:** Onboarding, POA filed, transcripts requested  
* **Hours 2–4:** 433-A hardship snapshot compiled; ACS call & case notes documented  
* **Hour 5:** **Levy release approved**; fax confirmation to bank  
* **Next banking day:** Bank shows **hold removed**; rent cleared`,

      `## Client quote
“**I thought my rent money was gone.** ATS got IRS to fax the release before the bank closed and told me exactly what to change so this doesn’t happen again.”`,

      `## Savings / impact
* **$2,750** in funds **protected** from sweep  
* Avoided **NSF/late fees** and eviction risk  
* **Lower stress** + a clear path to a small, affordable payment plan`,

      `## Why this worked
* **Time-sensitive escalation** during the bank’s **21-day levy hold** window  
* Clear, **documented hardship** (rent, childcare, transportation) via rapid 433-A  
* Coordinated **bank + IRS** communication and immediate follow-through on compliance`,

      `## What to have ready (checklist)
* **Last 2 bank statements** + current online balance screenshot  
* **Rent/lease**, **utilities**, **childcare** documentation  
* Most recent **pay stub** and side-gig income summary  
* Copies of **CP14/CP504** (or bank notice) and any recent IRS letters  
* A **realistic monthly amount** you can afford without missing essentials`,

      `### Who helped
**Company:** **American Tax Support (ATS)**  
**Team:** Jasmine K. (Intake), Owen L. (Case Manager), **Priya R., EA** (Advocacy)`,

      `### What to expect next
* Keep **withholding** and **EFTPS set-asides** on track  
* File/update any returns the EA requests (usually within 2–3 weeks)  
* Convert the temporary hold to a **direct-debit IA** if the balance stays above Fresh Start thresholds`,
    ],
  },

  // ─────────────── AMITY ───────────────
  {
    slug: "amity-state-irs-coordinated-plan",
    title: "Amity: Coordinated State + IRS Plan",
    companyKey: "AMITY",
    excerpt: "Sequencing state and federal relief to avoid conflicts.",
    taxType: "state",
    issues: ["State Coordination", "Lien"],
    resolution: "Coordinated Agreements",
    date: "2025-03-24",
    hero: "/assets/cases/state-irs.jpg",
    content: [
      `## Who had the problem
**Profile:** Multi-state sales rep based in **Colorado**, prior year moved from **Arizona**.  
**Household:** 2 adults, 1 dependent.  
**Cash flow:** Base salary + commissions (variable); employer in CO, prior withholding gaps in AZ.`,

      `## What was the problem
Balances with **both State (AZ DOR)** and the **IRS** after a relocation + under-withholding on commissions. State placed a **tax lien** and issued a **wage garnishment** notice; IRS was preparing to file a **NFTL** (federal lien).  
Sequence of notices:
* **State DOR:** Billing, *Final Notice of Intent to Garnish*, **Lien recorded**  
* **IRS:** CP14 (balance due), CP501/503 (reminders), **CP504** (intent to levy), NFTL warning

Client contacted **Amity Tax Group** once HR alerted them to the pending state garnishment.`,

      `## Balances at intake
**State (AZ DOR):** ≈ **$6,800** (tax **$5,100**, penalty/interest **$1,700**)  
**IRS:** ≈ **$19,400** (tax **$15,600**, penalty/interest **$3,800**)  
**Returns:** All filed, but W-4 and state withholding never updated during move; commissions spiked in Q3.`,

      `## Who they talked to (at the company)
* Intake coordinator: **Marisol T.** (same-day onboarding + transcript/account pulls)  
* Case manager: **Evan P.** (agency sequencing, payment calendar)  
* Enrolled Agent: **Lina C., EA** (state/IRS advocacy & compliance plan)`,

      `## Coordination strategy proposed
**Goal:** Prevent overlapping enforcement (**state wage order** + **federal lien/levy**), stabilize cash flow, and **sequence** agreements so one agency doesn’t derail the other.

**Plan:**
* **Phase 1 — State first** (because wage order imminent):  
  * Submit **hardship worksheet** + paystub/lease to AZ DOR  
  * Request **garnishment hold** pending **voluntary installment agreement (IA)**  
  * Make **good-faith payment** to trigger administrative pause  
* **Phase 2 — IRS second:**  
  * File **POA (2848)**, note pending state action in case history  
  * Propose **streamlined IRS IA** once state IA active so expenses reflect new state payment  
  * Update **W-4** and CO state withholding; set **EFTPS** calendar for commissions`,

      `## Resolution (what was implemented)
**State (AZ DOR):**
* **Garnishment hold** granted within 48 hours based on documentation  
* Approved **IA at $185/month**; **lien to be released** once principal < $1,000 or upon paid-in-full  
* **First payment drafted** same week

**IRS:**
* **Streamlined IA at $210/month** (considering state payment in allowable expenses)  
* **No levy** issued; client declined DDIA for now but set up **Direct Pay** calendar  
* IRS **NFTL filing deferred**; to be reconsidered if IA defaults`,

      `## Timing (how long it took)
* **Day 0:** Onboarding; POA filed (IRS); state financials uploaded  
* **Day 2:** State confirms **garnishment on hold**; good-faith payment posted  
* **Day 5:** **State IA approved**; payment schedule issued  
* **Day 9:** **IRS IA approved** after call noting state terms and new W-4  
* **Week 3:** Employer withholding updated; client portal shows both plans current`,

      `## Client quote
“**I was terrified both the state and IRS would hit my paycheck at once.** Amity sequenced everything so I could actually afford the payments and keep my job.”`,

      `## Savings / impact
* **Garnishment avoided** (would have taken ~**$900** from first check under AZ formula)  
* **No federal levy**; **NFTL deferred** pending IA compliance  
* Monthly outflow **planned**: **$185 (state) + $210 (IRS) = $395** with room for essentials`,

      `## Why this worked
* **Sequencing**: State first where enforcement was imminent; then IRS using updated expense picture  
* **Coordinated documentation**: Paystubs, lease, and commission history aligned across agencies  
* **Behavioral compliance**: W-4/withholding fixed + **EFTPS** calendar to prevent new accruals`,

      `## What to have ready (checklist)
* **Most recent paystubs**, prior **W-2/1099** and commission statements  
* **Lease/mortgage**, utilities, insurance, childcare invoices  
* Copies of **state and IRS letters** (final notices, liens)  
* Employer **HR/payroll contact** (for withholding updates)  
* A **realistic monthly number** you can maintain for 12–24 months`,

      `### Who helped
**Company:** **Amity Tax Group**  
**Team:** Marisol T. (Intake), Evan P. (Case Manager), **Lina C., EA** (Advocacy)`,

      `### What to expect next
* Maintain both payments for **6 months**, then review for **penalty abatement** possibilities  
* Request **state lien release** when threshold met or at paid-in-full  
* Annual check-in before filing season to **re-tune withholding** if commissions change`,
    ],
  },
  {
    slug: "amity-lien-withdrawal-ddia",
    title: "Amity: Lien Withdrawal via DDIA",
    companyKey: "AMITY",
    excerpt: "Direct-debit IA facilitating withdrawal request.",
    taxType: "income_1040",
    issues: ["Lien"],
    resolution: "DDIA + Withdrawal",
    date: "2025-03-31",
    hero: "/assets/cases/lien-withdrawal.jpg",
    content: [
      `## Who had the problem
**Profile:** W-2 operations manager in **Oregon** looking to refinance a mortgage.  
**Household:** 2 adults, 1 dependent.  
**Cash flow:** Stable salary with quarterly bonus; prior year under-withholding.`,

      `## What was the problem
An **IRS Notice of Federal Tax Lien (NFTL)** was filed ~10 months earlier. The client was on a **standard installment agreement** but had **two late payments** and was **denied a refinance** because of the lien.  
Goal: **Qualify for lien withdrawal** by moving to a **Direct-Debit Installment Agreement (DDIA)** and documenting good standing.`,

      `## Balance at intake
**Approx. balance:** **$17,800** (tax, penalty, interest combined).  
**Returns:** All filed; W-4 not tuned, causing shortfall the prior year.`,

      `## Who they talked to (at the company)
* Intake coordinator: **Ray A.** (records pull, transcript review)  
* Case manager: **Priya S.** (DDIA conversion, timelines)  
* Enrolled Agent: **Nate V., EA** (lien strategy & filing package)`,

      `## Strategy proposed
**Path to withdrawal (Fresh Start):**
* Convert existing IA to **DDIA** and keep it **current/on-time**  
* Update **withholding (W-4)** to stop new accruals  
* Prepare **Form 12277** (Application for Withdrawal of Filed NFTL) with support:
  * mortgage **refinance denial** letter
  * proof of **on-time DDIA drafts**
  * transcripts showing **compliance** (all returns filed, no new balances)
* If timing became critical, consider **subordination** as a backup; pursue **withdrawal** once DDIA shows good standing.`,

      `## Resolution (what was implemented)
* IA converted to **DDIA**; first draft scheduled in 10 days  
* **Withholding corrected** through employer portal; bonus withholding adjusted  
* After **three on-time drafts**, **Form 12277** submitted with exhibits  
* **Withdrawal approved**; IRS recorded the **Withdrawal of NFTL** and notified the credit bureaus / recording office.  
* Lender cleared file for **refinance** pending routine closing conditions.`,

      `## Timing (how long it took)
* **Day 0:** Onboarding, DDIA conversion, W-4 update  
* **Day 10:** 1st DDIA draft posts  
* **Day 40:** 2nd draft; refinance file pre-underwritten with lender letter pending  
* **Day 70:** 3rd draft; **Form 12277** sent with package  
* **Day 98:** **Withdrawal approved** by IRS  
* **~Day 110:** Public records/credit files reflect withdrawal; refinance scheduled`,

      `## Client quote
“**The lien was the only thing stopping our refinance.** Once the payments were on auto-draft, Amity handled the paperwork and the lender green-lit us.”`,

      `## Savings / impact
* **Refinance enabled** (lower rate + term), lowering housing cost by **~$230/mo**  
* **Credit profile improved** over the following reporting cycles  
* Reduced risk of future payment lapses via **automatic drafts**`,

      `## Why this worked
* **DDIA + on-time history** demonstrated good standing  
* **Clean compliance** (no new balances, all returns filed)  
* A **complete 12277 package** (lender denial, payment proof, transcripts) made approval straightforward`,

      `## What to have ready (checklist)
* Copy of the **lender denial** or conditional approval noting the lien  
* **Payment proof** (DDIA bank statements) and **IA confirmation**  
* Recent **wage statements** + updated **W-4**  
* **IRS transcripts** (account & return) or authorization for the firm to pull  
* Any prior IRS **correspondence** related to the lien`,

      `### Who helped
**Company:** **Amity Tax Group**  
**Team:** Ray A. (Intake), Priya S. (Case Manager), **Nate V., EA** (Representation)`,

      `### What to expect next
Maintain **on-time DDIA**; revisit in 6–12 months for potential **penalty relief** review. If income shifts, re-tune withholding to prevent new balances.`,
    ],
  },
  {
    slug: "amity-oic-doubt-as-to-collectibility",
    title: "Amity: OIC — Doubt as to Collectibility",
    companyKey: "AMITY",
    excerpt: "Allowable expense modeling and asset equity analysis.",
    taxType: "income_1040",
    issues: ["Offer in Compromise"],
    resolution: "OIC (DATC)",
    date: "2025-04-07",
    hero: "/assets/cases/oic-datc.jpg",
    content: [
      `## Who had the problem
**Profile:** Married couple in **California**; one W-2 income and a small seasonal 1099 side business.  
**Household:** 2 adults, 1 dependent.  
**Cash flow:** Net income varies; months of overtime followed by slow quarters.`,

      `## What was the problem
Unpaid balances accumulated from **2019–2023** due to under-withholding and irregular 1099 income.  
Client received **CP14**, then **CP501/503** reminders; **lien** was filed last year.  
Goal: pursue an **Offer in Compromise (DATC)** based on limited ability to pay.`,

      `## Balance at intake
**Approx. total:** **$64,200** (tax + penalties + interest).  
Older years approaching CSED; newest year recently assessed.  
All returns filed; needed W-4 tuning to stop new accruals.`,

      `## Who they talked to (at the company)
* Intake: **Lena H.** (records pull & transcripts)  
* Case manager: **Marco P.** (financial analysis & docs)  
* Enrolled Agent: **Iris K., EA** (OIC strategy & submission)  
**Company:** **Amity Tax Group**`,

      `## Strategy proposed
**DATC OIC with lump-sum terms** (to use the **12-month** future-income multiplier).  
Key steps:
* Stabilize current year: update **W-4** and set modest **ES payments** for 1099 income  
* Build a clean **433-A (OIC)** packet: 3–6 months bank statements, paystubs, bills  
* Model **allowable expenses** under IRS standards (housing, transportation, health)  
* Value assets at **quick-sale** basis and net out liens/loans; document why equity is limited  
* Prepare narrative on seasonal income and medical costs not obvious on transcripts  
* Identify source for **20% down** on offer (family gift) to enable lump-sum option`,

      `## Resolution (what was implemented)
* Submitted **Form 656** with **DATC** basis; proposed **$2,400** offer  
  * **Future income (12×)** from calculated MDI ≈ **$1,000** (about **$85/mo** after standards)  
  * **Net realizable equity** across checking, 10-year vehicle, small tools ≈ **$1,400**  
  * **RCP ≈ $2,400** ⇒ offer matched RCP  
* Examiner requested additional documentation (**AUR** of expenses + updated paystubs) at Week 12 → supplied within 7 days  
* **Offer accepted** at Month 7; **lien withdrawal** requested after lump-sum paid and payment posted  
* Client placed on **5-year compliance** reminders (file & pay on time; no new balances)`,

      `## Timing (how long it took)
* **Day 0–21:** Intake, W-4 update, documents gathered, financials modeled  
* **Day 30:** OIC filed with **20% down** ($480)  
* **Month 3:** Additional info request; response in 1 week  
* **Month 7:** **OIC accepted**; remaining lump-sum paid within 30 days  
* **Month 9:** **Lien withdrawal** recorded/credit files updated`,

      `## Client quote
“**Once they walked us through the numbers, the offer actually felt achievable.** The budget they used made sense to the examiner and stopped the spiral.”`,

      `## Savings / impact
* Liability **$64,200 → $2,400** (subject to full compliance for 5 years)  
* **Cash-flow stability** after W-4 fix; no new balances building  
* **Credit profile** improved as lien withdrawal propagated through bureaus`,

      `## Why this worked
* **Allowable expense** analysis aligned with IRS standards (no overreaching)  
* **Seasonal income** supported with deposits analysis and a clear narrative  
* **Asset equity** shown at **quick-sale** values with debts documented  
* Chose **lump-sum** terms to cut the future-income factor in half (12 vs 24)`,

      `## What to have ready (checklist)
* **Bank statements** (last 3–6 months) with essential expenses highlighted  
* **Paystubs**, proof of **withholding change**, and any 1099 ledgers  
* **Bills/receipts** for medical, childcare, insurance, transportation  
* **Titles/loan statements** for vehicles or tools; photos if condition affects value  
* Any **lien notices** and prior correspondence`,

      `## Who helped
**Amity Tax Group** — **Lena H.** (Intake), **Marco P.** (Case Manager), **Iris K., EA** (Representation)`,

      `## What to expect next
Stay **fully compliant for 5 years** (file on time; pay any amounts due).  
Revisit **withholding** each year and set quarterly estimates for any 1099 income.`,
    ],
  },

  // ──────────────── TGC ────────────────
  {
    slug: "tgc-trust-fund-defense-mapping",
    title: "TGC: Trust Fund Defense — Responsibility Mapping",
    companyKey: "TGC",
    excerpt: "Interviews, signatures, timelines to narrow exposure.",
    taxType: "payroll_941",
    issues: ["TFRP", "Appeals"],
    resolution: "TFRP Mitigation",
    date: "2025-04-18",
    hero: "/assets/cases/tfrp-mapping.jpg",
    content: [],
  },
  {
    slug: "tgc-appeals-penalty-abatement",
    title: "TGC: Appeals — Penalty Abatement Win",
    companyKey: "TGC",
    excerpt: "Reasonable cause + compliance narrative.",
    taxType: "business_1120_1065",
    issues: ["Appeals", "Penalties"],
    resolution: "Penalty Abatement",
    date: "2025-04-25",
    hero: "/assets/cases/appeals-penalty.jpg",
    content: [
      `## Who had the problem
**Profile:** Multi-member LLC in **California** (~18 employees) using a third-party payroll platform.  
**Industry:** Light manufacturing & installation.  
**Cash flow:** Erratic due to delayed receivables; missed several **941** deposits across three quarters.`,

      `## What was the problem
IRS initiated a **Trust Fund Recovery Penalty (TFRP)** investigation under **IRC §6672** after repeated 941 underpayments and late deposits.  
* Revenue Officer scheduled **Form 4180** interviews with owners and the controller  
* **Letter 1153** (proposed assessment) was anticipated; exposure could extend to multiple “responsible persons”  
* Records showed signature authority on bank/payroll for 3 individuals; unclear who actually controlled deposits`,

      `## Balance at intake
**Aggregate payroll liability at issue:** **$142,000** (trust fund portion ≈ **$82,000**) over **Q2–Q4** of the prior year.  
Lien filed. Business still operating; owners feared **personal assessment** and liens.`,

      `## Who they talked to (at the company)
* Intake: **Nadia V.** (document list & transcript pull)  
* Case manager: **Victor A.** (timeline + evidence matrix)  
* Enrolled Agent: **Priya S., EA** (4180 prep, Appeals strategy)  
**Company:** **Tax Group Consultants**`,

      `## Strategy proposed
**TFRP responsibility mapping** to narrow personal exposure and quarters:
* Build a **control matrix**: who had *actual* authority to pay creditors, sign checks, access **EFTPS**, hire/fire, direct accounting  
* Reconstruct **timelines**: who was in-office during each missed deposit, who authorized vendor payments over IRS, who handled payroll approvals  
* Separate **title** vs. **control** (e.g., “Managing Member” who never touched cash management)  
* Prepare **4180 interview packets**: role descriptions, email trails, delegations, PTO calendars, and payroll vendor logs  
* Identify **mitigation narratives**: illness, turnover, vendor platform change, bank holds, and how responsibility shifted mid-year  
* Preserve **Appeals rights**: if **Letter 1153** is issued, file protest within **60 days** to move to Appeals with a clean record`,

      `## Resolution (what was implemented)
* Conducted **witness interviews** (owners, controller, bookkeeper) and gathered **bank signature cards**, payroll platform admin logs, and **AP approval policies**  
* Presented Revenue Officer with a **responsibility map** showing one owner was *administrative only* (no AP control, no EFTPS access) and that **control shifted** in Q4  
* Result:
  * **Removed one owner** entirely from proposed responsible-person list  
  * **Limited quarters** for the remaining two: one limited to **Q2 only**; the other to **Q3–Q4**  
  * **Appeals** (on Letter 1153) conceded **$24,500** in trust-fund amount based on revised deposit records and vendor log timestamps  
* Business placed on **preferred deposit schedule** + cash-management SOP to prevent recurrence`,

      `## Timing (how long it took)
* **Week 0–2:** Intake, document map, and timeline reconstruction  
* **Week 3–4:** 4180 prep & interviews; initial position memo to RO  
* **Week 6:** **1153** issued naming 3; protest filed within 30 days with exhibits  
* **Month 4:** **Appeals conference**; scope narrowed, one party removed  
* **Month 6:** Final agreement reflecting limited responsible parties/quarters`,

      `## Client quote
“**Their matrix clarified who actually controlled payroll and when.** We walked into Appeals with a clean story instead of pointing fingers.”`,

      `## Savings / impact
* **One owner removed** from personal assessment entirely  
* **Quarters limited** for two individuals; **trust-fund base reduced by ~$24.5k**  
* Lower **personal exposure** → improved financing options and avoided immediate enforced collection against individuals`,

      `## Why this worked
* Focused on **actual control**, not job titles (emails, approvals, admin logs)  
* Clear **timeline** showed **mid-year shift** in authority and who could have paid IRS vs. other creditors  
* Early **4180 coaching** prevented damaging admissions and kept facts consistent across interviews  
* Preserved **Appeals** path with a **documented record** rather than unsupported claims`,

      `## What to have ready (checklist)
* **Form 4180** prep: accurate role descriptions for each potential responsible person  
* **Bank signature cards**, online banking admin roles, **EFTPS** access logs/screens  
* **Payroll vendor** admin history (who could release payroll & taxes)  
* **AP approvals**: who chose to pay vendors over IRS; email trails and policy docs  
* **Calendar evidence** (vacations, medical leave, turnover) to show who was present when deposits were missed`,

      `## Who helped
**Tax Group Consultants** — **Nadia V.** (Intake), **Victor A.** (Case Manager), **Priya S., EA** (Representation)`,

      `## What to expect next
Maintain **on-time deposits** for 6–12 months with documented controls.  
If cash flow tightens, coordinate **short-term IA** and vendor sequencing so trust-fund deposits are not skipped.`,
    ],
  },
  {
    slug: "tgc-multistate-sales-tax-vda",
    title: "TGC: Multi-State Sales Tax — Voluntary Disclosure",
    companyKey: "TGC",
    excerpt: "Nexus analysis and staged VDAs to control lookback.",
    taxType: "sales_use",
    issues: ["Sales/Use", "Multi-State"],
    resolution: "VDA + Payment Plan",
    date: "2025-05-02",
    hero: "/assets/cases/vda-sales-tax.jpg",
    content: [
      `## Who had the problem
**Profile:** Mid-market e-commerce & light distribution company shipping nationwide.  
**Footprint:** HQ in Nevada; 3 small warehouses (NV, TX, NJ); marketplace + DTC.  
**Systems:** Shopify + marketplace facilitator reports; outsourced fulfillment for East Coast.`,

      `## What was the problem
Post-Wayfair **economic nexus** and a mix of **physical presence** (inventory in 3PLs) created exposure in multiple states.  
* No prior sales/use registrations in several states despite thresholds exceeded  
* Marketplace facilitator rules partly covered marketplace sales, **but DTC remained taxable**  
* Drop-ship/vendor scenarios in a few states complicated exemption certificates  
* States began mailing inquiry letters; risk of **lookback beyond 4 years** plus **penalties**`,

      `## Exposure at intake (estimate)
**Uncollected tax (multi-state):** ≈ **$410,000** across **8 states**  
**Penalties/interest (gross):** ≈ **$120,000–$180,000** (varies by state)  
**Drivers:** economic nexus (> \$100k sales) in TX/FL/NY; inventory nexus in NJ/TX; click-through nexus suspected in IL.`,

      `## Who they talked to (at the company)
* Intake & scoping: **Sonia P.** (document checklist, data pulls)  
* Project lead: **Marcos G.** (state sequencing, cash-flow modeling)  
* State filings & negotiations: **Elena D., EA** (VDA coordinator)  
**Company:** **Tax Group Consultants**`,

      `## Strategy proposed
**Multi-state VDA program**, staged by risk and cash-flow:
* **Prioritize high-risk states** (clear nexus + inquiry letters) → submit **anonymous VDA** where available  
* Use VDA to **limit lookback** (typically **3–4 years**) and secure **penalty waiver**  
* **Segment marketplace vs DTC** sales; rely on facilitator collection where lawful; register DTC prospectively  
* Build **item taxability matrix** + exemption certificate process for B2B drop-ship  
* **Payment plans** in larger states; lump-sum where balances are modest  
* Standardize **future compliance** (registrations, filing calendar, data pipeline from Shopify/3PL)`,

      `## Resolution (what was implemented)
* Filed **anonymous VDAs** in **TX, FL, NY, NJ, IL**; standard registrations in **WA, CO, CA** (no VDA)  
* Achieved **lookback limited to 3–4 years** in VDA states with **full penalty abatement**  
* Segregated marketplace-collected sales and removed from liability where allowed  
* Established **DTC collection** prospectively; implemented **Avalara-style tax mapping** (or equivalent)  
* Entered **installment agreements** in TX & NY (12–18 months); paid FL/NJ lump-sum after negotiated interest review  
* Deployed **certificate management** for B2B customers; trained CS on exemption validation`,

      `## Timing (how long it took)
* **Weeks 0–2:** Data reconciliation (states, channels, by month), nexus memo, sequencing plan  
* **Weeks 3–6:** Anonymous VDA submissions (TX, FL, NY); registrations in WA/CO/CA; NJ/IL queued  
* **Weeks 8–12:** State confirmations; lookback windows set; draft assessments issued  
* **Months 3–5:** Payment plans approved; lump-sums remitted; all accounts live for prospective filing`,

      `## Client quote
“**We knew we were exposed but not how to tackle eight states at once.** TGC staged the filings, cut penalties, and kept cash flow sane.”`,

      `## Savings / impact
* **Penalty abatement:** ≈ **\$95,000** waived via VDAs (varies by state)  
* **Lookback control:** avoided potential **6–7 year** reach in inquiry states → liability reduced ≈ **\$60,000–\$85,000**  
* **Cash-flow protection:** **installments** on the two largest balances instead of lump-sum strain  
* **Future risk down:** registrations + filing cadence in place; marketplace/DTC split correctly handled`,

      `## Why this worked
* **Anonymous VDAs** created a safe path to disclose while limiting lookback and waiving penalties  
* Clean **channel separation** (marketplace vs DTC) avoided double-collection  
* Documented **inventory/3PL presence** clarified where physical nexus actually existed  
* Early **payment plan** discussions preserved cash flow while clearing historic risk`,

      `## What to have ready (checklist)
* **Monthly sales by state**, split **marketplace vs DTC** (gross, taxable, exempt)  
* **Inventory/3PL locations** and dates; any in-state personnel or contractors  
* Product **taxability matrix** (SaaS vs tangible, kits, shipping/handling policies)  
* **Exemption certificates** for B2B customers; process to collect/validate going forward  
* Prior **state letters** or notices, if any`,

      `## Who helped
**Tax Group Consultants** — **Sonia P.** (Intake), **Marcos G.** (Project Lead), **Elena D., EA** (VDA Coordinator)`,

      `## What to expect next
Maintain **on-time filings** in all active states; review **nexus quarterly** for new thresholds or facility changes.  
Re-evaluate **item taxability** annually and keep **certificate management** current to avoid unexpected assessments.`,
    ],
  },
];
